import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import config from "../../../config";
import { fetchApi } from "../marketing/access";

/**
 * Announcements the Olli team manages in the admin panel (old dashboard's hero
 * slider, PlatformDashboard.js): GET /api/dashboard-slides → active slides
 * [{ id, heading, description, image_url, link_url, cta_label, sort_order }].
 * The Home design has no slot for them, so they sit as one compact band at the
 * top; it rotates every 6 s (paused on hover or focus) and is left out when
 * there are no slides. A failed load leaves it out too: it is a notice board,
 * not clinic data, and the rest of Home doesn't depend on it.
 */

const ROTATE_MS = 6000;
const imageBase = (config.API_BASE_URL || "").replace(/\/api\/?$/, "");
const imageOf = (url) => (!url ? null : /^(https?:|data:)/.test(url) ? url : `${imageBase}${url.startsWith("/") ? "" : "/"}${url}`);

export function HeroSlides() {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // An image that doesn't load is left out; the text still shows.
  const [broken, setBroken] = useState({});

  useEffect(() => {
    let alive = true;
    fetchApi("/api/dashboard-slides").then((res) => {
      if (!alive) return;
      if (res.ok) setSlides(Array.isArray(res.data?.data) ? res.data.data.filter((s) => s.heading || s.image_url) : []);
      else console.warn("[home] dashboard slides unavailable:", res.message || res.status);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (slides.length <= 1 || paused) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [slides.length, paused]);

  if (!slides.length) return null;
  const slide = slides[Math.min(index, slides.length - 1)];
  const go = (url) => {
    if (!url) return;
    if (/^https?:\/\//.test(url)) window.open(url, "_blank", "noopener,noreferrer");
    else navigate(url);
  };
  const step = (by) => setIndex((i) => (i + by + slides.length) % slides.length);
  const image = imageOf(slide.image_url);

  return (
    <section aria-label="Announcements" aria-roledescription="carousel" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} className="relative flex min-w-0 items-stretch overflow-hidden rounded-lg bg-paper shadow-soft">
      {image && !broken[slide.id] && <img key={slide.id} src={image} alt="" draggable="false" onError={() => setBroken((b) => ({ ...b, [slide.id]: true }))} className="animate-fade-in h-auto w-24 shrink-0 select-none object-cover sm:w-48" />}
      <div key={`t-${slide.id}`} className="animate-fade-in flex min-w-0 flex-1 flex-col justify-center gap-1 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-small font-semibold text-ink sm:text-body">{slide.heading}</p>
          {slide.description && <p className="mt-0.5 line-clamp-2 text-caption text-muted sm:text-small">{slide.description}</p>}
        </div>
        {slide.link_url && (
          <button type="button" onClick={() => go(slide.link_url)} className="inline-flex h-9 w-fit shrink-0 items-center gap-1.5 rounded-full bg-ink px-4 text-caption font-semibold text-paper hover:bg-ink/85">
            {slide.cta_label || "Learn more"}
            <ArrowRight size={14} strokeWidth={1.75} />
          </button>
        )}
      </div>
      {slides.length > 1 && (
        <div className="flex shrink-0 flex-col items-center justify-center gap-1 border-l border-line px-1.5 sm:flex-row sm:px-3">
          <button type="button" onClick={() => step(-1)} aria-label="Previous announcement" className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink"><ChevronLeft size={16} strokeWidth={1.75} /></button>
          <span className="text-caption tabular-nums text-muted" aria-live="polite">{index + 1}/{slides.length}</span>
          <button type="button" onClick={() => step(1)} aria-label="Next announcement" className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink"><ChevronRight size={16} strokeWidth={1.75} /></button>
        </div>
      )}
    </section>
  );
}
